// The Calendar of Harptos - Faerûnian Calendar System
// Used in the Forgotten Realms D&D setting

export const FAERUNIAN_MONTHS = [
  { 
    number: 1, 
    name: 'Hammer', 
    commonName: 'Deepwinter',
    days: 30,
    season: 'Winter',
    description: 'The coldest month, deep in winter\'s grip'
  },
  { 
    number: 2, 
    name: 'Alturiak', 
    commonName: 'The Claw of Winter',
    days: 30,
    season: 'Winter',
    description: 'Winter\'s last fierce grip, also called the Claws of the Cold'
  },
  { 
    number: 3, 
    name: 'Ches', 
    commonName: 'The Claw of the Sunsets',
    days: 30,
    season: 'Spring',
    description: 'Early spring, when the sun sets later each day'
  },
  { 
    number: 4, 
    name: 'Tarsakh', 
    commonName: 'The Claw of the Storms',
    days: 30,
    season: 'Spring',
    description: 'Spring storms bring life-giving rain'
  },
  { 
    number: 5, 
    name: 'Mirtul', 
    commonName: 'The Melting',
    days: 30,
    season: 'Spring',
    description: 'Late spring, when the last snow melts'
  },
  { 
    number: 6, 
    name: 'Kythorn', 
    commonName: 'The Time of Flowers',
    days: 30,
    season: 'Summer',
    description: 'Early summer, flowers bloom everywhere'
  },
  { 
    number: 7, 
    name: 'Flamerule', 
    commonName: 'Summertide',
    days: 30,
    season: 'Summer',
    description: 'High summer, the hottest time of year'
  },
  { 
    number: 8, 
    name: 'Eleasis', 
    commonName: 'Highsun',
    days: 30,
    season: 'Summer',
    description: 'Late summer, when the sun is highest'
  },
  { 
    number: 9, 
    name: 'Eleint', 
    commonName: 'The Fading',
    days: 30,
    season: 'Autumn',
    description: 'Early autumn, summer\'s warmth fades'
  },
  { 
    number: 10, 
    name: 'Marpenoth', 
    commonName: 'Leaffall',
    days: 30,
    season: 'Autumn',
    description: 'Mid-autumn, leaves change and fall'
  },
  { 
    number: 11, 
    name: 'Uktar', 
    commonName: 'The Rotting',
    days: 30,
    season: 'Autumn',
    description: 'Late autumn, fallen leaves decay'
  },
  { 
    number: 12, 
    name: 'Nightal', 
    commonName: 'The Drawing Down',
    days: 30,
    season: 'Winter',
    description: 'Early winter, nights grow long'
  }
];

// Special holidays that fall between months (not counted in month days)
export const FAERUNIAN_HOLIDAYS = [
  {
    name: 'Midwinter',
    alternateNames: ['Deadwinter Day'],
    afterMonth: 1, // After Hammer
    description: 'Festival marking the midpoint of winter, a day of warm celebrations',
    traditions: 'Feasting, storytelling, and renewing alliances',
    type: 'festival'
  },
  {
    name: 'Greengrass',
    alternateNames: ['The Great Rite of Spring'],
    afterMonth: 4, // After Tarsakh
    description: 'Spring festival celebrating the return of life and growth',
    traditions: 'Planting ceremonies, romantic declarations, flower crowns',
    type: 'festival'
  },
  {
    name: 'Midsummer',
    alternateNames: ['Summerfest'],
    afterMonth: 7, // After Flamerule
    description: 'The longest day of the year, celebrations last all night',
    traditions: 'Bonfires, music, dancing, love declarations, magical displays',
    type: 'festival'
  },
  {
    name: 'Shieldmeet',
    alternateNames: ['The Festival of the Moon'],
    afterMonth: 7, // After Midsummer, every 4 years
    leapDay: true,
    description: 'Occurs once every four years, a day for renewal of pacts',
    traditions: 'Tournaments, renewal of agreements, great councils',
    type: 'special'
  },
  {
    name: 'Highharvestide',
    alternateNames: ['The Feast of the Harvest'],
    afterMonth: 9, // After Eleint
    description: 'Harvest festival marking the autumn bounty',
    traditions: 'Feasting, trading, paying debts, giving thanks',
    type: 'festival'
  },
  {
    name: 'The Feast of the Moon',
    alternateNames: ['Moonfest'],
    afterMonth: 11, // After Uktar
    description: 'Festival honoring the dead and remembering ancestors',
    traditions: 'Honoring the dead, telling legends, quiet reflection',
    type: 'solemn'
  }
];

// Notable religious and regional holidays
export const REGIONAL_HOLIDAYS = {
  // Waterdeep
  'Liar\'s Night': {
    date: { month: 11, day: 30 },
    description: 'Night of masks and pranks in Waterdeep',
    region: 'Waterdeep'
  },
  'Ahghairon\'s Day': {
    date: { month: 8, day: 1 },
    description: 'Celebrates the birthday of Waterdeep\'s most famous wizard',
    region: 'Waterdeep'
  },
  
  // Religious
  'The Ritual of the Unseen Presence': {
    date: { month: 1, day: 15 },
    description: 'Holy day of Helm, god of guardians',
    deity: 'Helm'
  },
  'The Day of Wonders': {
    date: { month: 5, day: 15 },
    description: 'Holy day of Gond, god of craft and invention',
    deity: 'Gond'
  },
  'The Reign of Misrule': {
    date: { month: 12, day: 15 },
    description: 'Day sacred to Beshaba, goddess of misfortune',
    deity: 'Beshaba'
  }
};

// Days of the tenday (Faerûnian week)
export const TENDAY_NAMES = [
  'Firstday',
  'Secondday',
  'Thirdday',
  'Fourthday',
  'Fifthday',
  'Sixthday',
  'Seventhday',
  'Eighthday',
  'Ninthday',
  'Tenthday'
];

// Class to handle Faerûnian date calculations
export class FaerunianDate {
  constructor(year = 1492, dayOfYear = 1) {
    this.year = year; // Default: 1492 DR (Dungeons & Dragons 5e timeline)
    this.dayOfYear = dayOfYear;
    this.calculate();
  }

  calculate() {
    let remainingDays = this.dayOfYear;
    let currentMonth = 0;
    let isHoliday = false;
    let holidayName = null;
    
    for (let i = 0; i < FAERUNIAN_MONTHS.length; i++) {
      const month = FAERUNIAN_MONTHS[i];
      
      // Check for holiday after this month
      const holiday = FAERUNIAN_HOLIDAYS.find(h => h.afterMonth === month.number && !h.leapDay);
      
      if (remainingDays <= month.days) {
        currentMonth = i;
        this.month = month;
        this.day = remainingDays;
        break;
      }
      
      remainingDays -= month.days;
      
      if (holiday && remainingDays === 1) {
        isHoliday = true;
        holidayName = holiday.name;
        this.month = month;
        this.day = null;
        break;
      } else if (holiday && remainingDays > 0) {
        remainingDays -= 1;
      }
    }
    
    this.isHoliday = isHoliday;
    this.holidayName = holidayName;
    
    // Calculate tenday
    if (this.day) {
      this.tenday = Math.ceil(this.day / 10);
      this.dayOfTenday = ((this.day - 1) % 10) + 1;
      this.tendayName = TENDAY_NAMES[this.dayOfTenday - 1];
    }
  }

  // Add days to current date
  addDays(days) {
    this.dayOfYear += days;
    while (this.dayOfYear > 365) {
      this.dayOfYear -= 365;
      this.year += 1;
    }
    this.calculate();
    return this;
  }

  // Format date for display
  toString(format = 'full') {
    if (this.isHoliday) {
      return `${this.holidayName}, ${this.year} DR`;
    }
    
    switch (format) {
      case 'short':
        return `${this.day} ${this.month.name}, ${this.year}`;
      case 'long':
        return `${this.tendayName}, ${this.day}${this.getOrdinalSuffix(this.day)} of ${this.month.name}, ${this.year} DR`;
      case 'full':
      default:
        return `${this.tendayName}, the ${this.day}${this.getOrdinalSuffix(this.day)} of ${this.month.name} (${this.month.commonName}), ${this.year} DR`;
    }
  }

  // Get pretty formatted date for journals
  toJournalFormat() {
    if (this.isHoliday) {
      return {
        primary: this.holidayName,
        secondary: `Year ${this.year} DR`,
        season: FAERUNIAN_MONTHS[this.month.number - 1].season,
        isSpecial: true
      };
    }
    
    return {
      primary: `${this.day}${this.getOrdinalSuffix(this.day)} of ${this.month.name}`,
      secondary: `${this.month.commonName}, ${this.year} DR`,
      season: this.month.season,
      isSpecial: false
    };
  }

  // Get ordinal suffix for day
  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  // Check if current date is a holiday
  checkHolidays() {
    const holidays = [];
    
    // Check special holidays
    if (this.isHoliday) {
      const holiday = FAERUNIAN_HOLIDAYS.find(h => h.name === this.holidayName);
      if (holiday) holidays.push(holiday);
    }
    
    // Check regional holidays
    Object.entries(REGIONAL_HOLIDAYS).forEach(([name, holiday]) => {
      if (holiday.date.month === this.month.number && holiday.date.day === this.day) {
        holidays.push({ name, ...holiday });
      }
    });
    
    return holidays;
  }

  // Get zodiac sign (if you want to add Faerûnian zodiac)
  getZodiacSign() {
    // Simplified Faerûnian zodiac
    const zodiacs = [
      { name: 'The Trident', start: 1, end: 30 },
      { name: 'The Fool', start: 31, end: 60 },
      { name: 'The Rider', start: 61, end: 90 },
      { name: 'The Patriarch', start: 91, end: 120 },
      { name: 'The Sage', start: 121, end: 150 },
      { name: 'The Serpent', start: 151, end: 180 },
      { name: 'The Warrior', start: 181, end: 210 },
      { name: 'The Maiden', start: 211, end: 240 },
      { name: 'The Scales', start: 241, end: 270 },
      { name: 'The Dragon', start: 271, end: 300 },
      { name: 'The Horse', start: 301, end: 330 },
      { name: 'The Hawk', start: 331, end: 365 }
    ];
    
    return zodiacs.find(z => this.dayOfYear >= z.start && this.dayOfYear <= z.end);
  }

  // Get moon phase (simplified)
  getMoonPhase() {
    const phases = ['New Moon', 'Waxing Crescent', 'First Quarter', 'Waxing Gibbous', 
                   'Full Moon', 'Waning Gibbous', 'Last Quarter', 'Waning Crescent'];
    const dayInCycle = this.dayOfYear % 30;
    const phaseIndex = Math.floor((dayInCycle / 30) * 8);
    return phases[phaseIndex];
  }

  // Static method to create from game day
  // [FIX - Week 2, Day 6]: Handle negative game days
  static fromGameDay(gameDay, startYear = 1492) {
    // Ensure gameDay is at least 1 to prevent negative dates
    const safeGameDay = Math.max(1, gameDay);
    const year = startYear + Math.floor((safeGameDay - 1) / 365);
    const dayOfYear = ((safeGameDay - 1) % 365) + 1;
    return new FaerunianDate(year, dayOfYear);
  }
}

// Get season colors for UI theming
export function getSeasonColor(season) {
  switch (season) {
    case 'Winter': return 'text-blue-400 bg-blue-900';
    case 'Spring': return 'text-green-400 bg-green-900';
    case 'Summer': return 'text-yellow-400 bg-yellow-900';
    case 'Autumn': return 'text-orange-400 bg-orange-900';
    default: return 'text-gray-400 bg-gray-900';
  }
}

// Get holiday icon
export function getHolidayIcon(holidayType) {
  switch (holidayType) {
    case 'festival': return '🎉';
    case 'solemn': return '🕯️';
    case 'special': return '⭐';
    default: return '📅';
  }
}

// Export default
export default {
  FAERUNIAN_MONTHS,
  FAERUNIAN_HOLIDAYS,
  REGIONAL_HOLIDAYS,
  TENDAY_NAMES,
  FaerunianDate,
  getSeasonColor,
  getHolidayIcon
};